import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useCallback } from "react";
import { EmailApiError, type EmailDataProvider } from "../../email/api/types";

export type ConsentRequest = {
  email: string;
  companyId: number;
  callId: number;
  obtainedAt: string;
  /** The outcome was queued offline: the consent is NOT sent, the rep must redo it. */
  offline: boolean;
};

/**
 * « A accepté de recevoir nos courriels » → express consent through the email API
 * (batch 5 emailRecordConsent → SQL record_consent). Never a raw `consents` insert.
 */
export function useRecordConsent() {
  const dp = useDataProvider<EmailDataProvider>();
  const notify = useNotify();
  const t = useTranslate();
  return useCallback(
    async (r: ConsentRequest) => {
      if (r.offline) {
        notify("gavion.dialer.form.consent_offline", { type: "warning" });
        return;
      }
      const reason = (code: string) =>
        t(`gavion.email.common.errors.${code}`, {
          _: t("gavion.dialer.errors.unknown"),
        });
      if (typeof dp.emailRecordConsent !== "function") {
        notify("gavion.dialer.form.consent_failed", {
          type: "warning",
          messageArgs: { reason: reason("unknown") },
        });
        return;
      }
      try {
        await dp.emailRecordConsent({
          email: r.email,
          basis: "express",
          evidence: `Agreed by phone during dialer call #${r.callId}`,
          obtainedAt: r.obtainedAt,
          companyId: r.companyId,
        });
      } catch (e) {
        notify("gavion.dialer.form.consent_failed", {
          type: "warning",
          messageArgs: {
            reason: reason(e instanceof EmailApiError ? e.code : "unknown"),
          },
        });
      }
    },
    [dp, notify, t],
  );
}
